import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/base/PageHeader';
import StatusBadge from '@/components/base/StatusBadge';
import ProgressBar from '@/components/base/ProgressBar';
import Card from '@/components/base/Card';
import EmptyState from '@/components/base/EmptyState';
import RowActions from '@/components/base/RowActions';
import { projectStatuses, projectCategories } from '@/types';
import { useData } from '@/context/DataContext';
import { formatTL, formatDate } from '@/lib/format';
import { useApp } from '@/context/AppContext';

export default function Projects() {
  const navigate = useNavigate();
  const { openCreate, openEdit, openDelete } = useApp();
  const { projects, customerName } = useData();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [view, setView] = useState<'liste' | 'kart'>('liste');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return projects.filter((p) => {
      const matchesQuery = !q || p.name.toLowerCase().includes(q) || p.id.toLowerCase().includes(q) || customerName(p.customerId).toLowerCase().includes(q);
      const matchesStatus = !status || p.status === status;
      const matchesCategory = !category || p.category === category;
      return matchesQuery && matchesStatus && matchesCategory;
    });
  }, [query, status, category, projects, customerName]);

  return (
    <div className="space-y-5 animate-fade-in-up">
      <PageHeader
        title="İşler / Projeler"
        description={`${filtered.length} proje listeleniyor`}
        actions={
          <button
            type="button"
            onClick={() => openCreate('project')}
            className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
          >
            <i className="ri-add-line text-lg" /> Yeni Proje
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
          <i className="ri-search-line text-base text-foreground-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Proje adı, no, müşteri ara..."
            className="h-9 w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700">
          <option value="">Tüm Durumlar</option>
          {projectStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 max-w-[200px] cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700">
          <option value="">Tüm Kategoriler</option>
          {projectCategories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <div className="ml-auto inline-flex items-center rounded-md border border-background-300 bg-background-50 p-0.5">
          <button type="button" onClick={() => setView('liste')} className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded ${view === 'liste' ? 'bg-primary-500 text-white' : 'text-foreground-500 hover:text-foreground-900'}`}>
            <i className="ri-list-check-2" />
          </button>
          <button type="button" onClick={() => setView('kart')} className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded ${view === 'kart' ? 'bg-primary-500 text-white' : 'text-foreground-500 hover:text-foreground-900'}`}>
            <i className="ri-layout-grid-line" />
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="Proje bulunamadı" description="Filtreleri değiştirerek tekrar deneyin." />
      ) : view === 'liste' ? (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/50 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                  <th className="px-5 py-3">Proje</th>
                  <th className="px-4 py-3">Müşteri</th>
                  <th className="px-4 py-3">Kategori</th>
                  <th className="px-4 py-3">Sorumlu</th>
                  <th className="px-4 py-3">İlerleme</th>
                  <th className="px-4 py-3">Teslim</th>
                  <th className="px-4 py-3">Durum</th>
                  <th className="px-4 py-3 text-right">Satış</th>
                  <th className="px-4 py-3 text-right">Kalan Alacak</th>
                  <th className="px-4 py-3 text-right">Kâr</th>
                  <th className="px-4 py-3 text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const remaining = p.salesAmount - p.collected;
                  const profit = p.salesAmount - p.cost;
                  return (
                    <tr key={p.id} onClick={() => navigate(`/projects/${p.id}`)} className="cursor-pointer border-b border-background-100 transition-colors last:border-0 hover:bg-background-100/60">
                      <td className="px-5 py-3.5">
                        <p className="font-semibold text-foreground-900">{p.name}</p>
                        <p className="text-xs text-foreground-500">{p.id}</p>
                      </td>
                      <td className="px-4 py-3.5 text-foreground-700">{customerName(p.customerId)}</td>
                      <td className="px-4 py-3.5 text-foreground-600">{p.category}</td>
                      <td className="px-4 py-3.5 text-foreground-700">{p.responsible}</td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2">
                          <ProgressBar value={p.progress} className="w-20" />
                          <span className="text-xs font-semibold text-foreground-600">%{p.progress}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-foreground-600">{formatDate(p.plannedDelivery)}</td>
                      <td className="px-4 py-3.5"><StatusBadge status={p.status} /></td>
                      <td className="px-4 py-3.5 text-right font-semibold text-foreground-900">{formatTL(p.salesAmount)}</td>
                      <td className="px-4 py-3.5 text-right font-semibold text-accent-700">{remaining > 0 ? formatTL(remaining) : '-'}</td>
                      <td className="px-4 py-3.5 text-right font-semibold text-primary-700">{formatTL(profit)}</td>
                      <td className="px-4 py-3.5"><RowActions onEdit={() => openEdit('project', p)} onDelete={() => openDelete('project', p.id, p.name)} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="stagger grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((p) => {
            const remaining = p.salesAmount - p.collected;
            const profit = p.salesAmount - p.cost;
            return (
              <Card key={p.id} onClick={() => navigate(`/projects/${p.id}`)} className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-500">{p.id}</p>
                    <h3 className="mt-0.5 truncate font-heading text-base font-semibold text-foreground-950">{p.name}</h3>
                    <p className="mt-0.5 truncate text-xs text-foreground-500">{customerName(p.customerId)} · {p.category}</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <StatusBadge status={p.status} />
                    <RowActions onEdit={() => openEdit('project', p)} onDelete={() => openDelete('project', p.id, p.name)} />
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs text-foreground-500">
                    <span>İlerleme</span>
                    <span className="font-semibold text-foreground-900">%{p.progress}</span>
                  </div>
                  <ProgressBar value={p.progress} className="mt-1.5" />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 border-t border-background-200 pt-4 text-sm">
                  <div><p className="text-xs text-foreground-500">Satış</p><p className="font-semibold text-foreground-900">{formatTL(p.salesAmount)}</p></div>
                  <div><p className="text-xs text-foreground-500">Kalan Alacak</p><p className="font-semibold text-accent-700">{remaining > 0 ? formatTL(remaining) : '-'}</p></div>
                  <div><p className="text-xs text-foreground-500">Maliyet</p><p className="font-semibold text-foreground-900">{formatTL(p.cost)}</p></div>
                  <div><p className="text-xs text-foreground-500">Tahmini Kâr</p><p className="font-semibold text-primary-700">{formatTL(profit)}</p></div>
                </div>
                <div className="mt-4 flex items-center justify-between text-xs text-foreground-500">
                  <span>Sorumlu: {p.responsible}</span>
                  <span>Teslim: {formatDate(p.plannedDelivery)}</span>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}